'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface AppImageProps {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    priority?: boolean;
    quality?: number;
    placeholder?: 'blur' | 'empty';
    blurDataURL?: string;
    fill?: boolean;
    sizes?: string;
    onClick?: () => void;
    fallbackSrc?: string;
    loading?: 'lazy' | 'eager';
    responsive?: boolean;
    [key: string]: any;
}

// Generate responsive sizes string based on common breakpoints
const generateResponsiveSizes = (defaultSize: string = '100vw'): string => {
    return '(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw';
};

// Generate blur placeholder for better loading experience
const shimmer = (w: number, h: number) => `
<svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs>
    <linearGradient id="g">
      <stop stop-color="#1a1a1a" offset="20%" />
      <stop stop-color="#2a2a2a" offset="50%" />
      <stop stop-color="#1a1a1a" offset="70%" />
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="#1a1a1a" />
  <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
</svg>`;

const toBase64 = (str: string) =>
    typeof window === 'undefined' ? Buffer.from(str).toString('base64')
        : window.btoa(str);

function AppImage({
    src,
    alt,
    width,
    height,
    className = '',
    priority = false,
    quality = 90,
    placeholder = 'blur',
    blurDataURL,
    fill = false,
    sizes,
    onClick,
    fallbackSrc = '/assets/images/no_image.png',
    loading = 'lazy',
    responsive = true,
    ...props
}: AppImageProps) {
    const [imageSrc, setImageSrc] = useState(src);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    // More reliable external URL detection
    const isExternal = imageSrc.startsWith('http://') || imageSrc.startsWith('https://');
    const isLocal = imageSrc.startsWith('/') || imageSrc.startsWith('./') || imageSrc.startsWith('data:');

    // Generate blur placeholder if not provided
    const defaultBlurDataURL = !blurDataURL && width && height
        ? `data:image/svg+xml;base64,${toBase64(shimmer(width, height))}`
        : blurDataURL;

    const handleError = () => {
        if (!hasError && imageSrc !== fallbackSrc) {
            setImageSrc(fallbackSrc);
            setHasError(true);
        }
        setIsLoading(false);
    };

    const handleLoad = () => {
        setIsLoading(false);
        setHasError(false);
    };

    const commonClassName = `${className} ${
        isLoading ? 'bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-pulse' : ''
    } ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''} ${
        !isLoading && !hasError ? 'transition-all duration-500' : ''
    }`;

    // Generate responsive sizes if enabled
    const responsiveSizes = responsive && !sizes ? generateResponsiveSizes() : sizes;

    // For external URLs or when in doubt, use regular img tag with optimization
    if (isExternal && !isLocal) {
        const imgStyle: React.CSSProperties = {};

        if (width) imgStyle.width = width;
        if (height) imgStyle.height = height;

        if (fill) {
            return (
                <div data-component-id="src/components/ui/AppImage.tsx:111:16" data-component-path="src/components/ui/AppImage.tsx" data-component-line="111" data-component-end-line="128" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative ${className}`} style={{ width: width || '100%', height: height || '100%' }}>
                    <img data-component-id="src/components/ui/AppImage.tsx:112:20" data-component-path="src/components/ui/AppImage.tsx" data-component-line="112" data-component-end-line="123" data-component-file="AppImage.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%5Bvar%3AimageSrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                        src={imageSrc}
                        alt={alt}
                        className={`${commonClassName} absolute inset-0 w-full h-full object-cover`}
                        onError={handleError}
                        onLoad={handleLoad}
                        onClick={onClick}
                        loading={loading}
                        decoding="async"
                        style={imgStyle}
                        {...props}
                    />
                    {/* Loading overlay */}
                    {isLoading && (
                        <div data-component-id="src/components/ui/AppImage.tsx:126:24" data-component-path="src/components/ui/AppImage.tsx" data-component-line="126" data-component-end-line="126" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-gradient-to-r%20from-gray-800%20via-gray-700%20to-gray-800%20animate-pulse%22%7D" className="absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-pulse" />
                    )}
                </div>
            );
        }

        return (
            <>
                <img data-component-id="src/components/ui/AppImage.tsx:134:16" data-component-path="src/components/ui/AppImage.tsx" data-component-line="134" data-component-end-line="145" data-component-file="AppImage.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%5Bvar%3AimageSrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22className%22%3A%22%5Bvar%3AcommonClassName%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                    src={imageSrc}
                    alt={alt}
                    className={commonClassName}
                    onError={handleError}
                    onLoad={handleLoad}
                    onClick={onClick}
                    loading={loading}
                    decoding="async"
                    style={imgStyle}
                    {...props}
                />
            </>
        );
    }

    // For local images and data URLs, use Next.js Image component with optimizations
    const imageProps = {
        src: imageSrc,
        alt,
        className: commonClassName,
        priority,
        quality,
        placeholder: defaultBlurDataURL ? ('blur' as const) : ('empty' as const),
        blurDataURL: defaultBlurDataURL,
        unoptimized: false, // Enable Next.js optimization for better performance
        onError: handleError,
        onLoad: handleLoad,
        onClick,
        loading: priority ? undefined : loading,
        ...props,
    };

    if (fill) {
        return (
            <div data-component-id="src/components/ui/AppImage.tsx:169:12" data-component-path="src/components/ui/AppImage.tsx" data-component-line="169" data-component-end-line="180" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative ${className}`}>
                <Image data-component-id="src/components/ui/AppImage.tsx:170:16" data-component-path="src/components/ui/AppImage.tsx" data-component-line="170" data-component-end-line="175" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                    {...imageProps}
                    fill
                    sizes={responsiveSizes || '100vw'}
                    style={{ objectFit: 'cover' }}
                />
                {/* Loading overlay */}
                {isLoading && (
                    <div data-component-id="src/components/ui/AppImage.tsx:178:20" data-component-path="src/components/ui/AppImage.tsx" data-component-line="178" data-component-end-line="178" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-gradient-to-r%20from-gray-800%20via-gray-700%20to-gray-800%20animate-pulse%22%7D" className="absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-pulse" />
                )}
            </div>
        );
    }

    return (
        <div data-component-id="src/components/ui/AppImage.tsx:185:8" data-component-path="src/components/ui/AppImage.tsx" data-component-line="185" data-component-end-line="199" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20inline-block%22%7D" className="relative inline-block">
            <Image data-component-id="src/components/ui/AppImage.tsx:186:12" data-component-path="src/components/ui/AppImage.tsx" data-component-line="186" data-component-end-line="191" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                {...imageProps}
                width={width || 400}
                height={height || 300}
                sizes={responsiveSizes}
            />
            {/* Loading overlay for fixed size images */}
            {isLoading && (
                <div data-component-id="src/components/ui/AppImage.tsx:194:16" data-component-path="src/components/ui/AppImage.tsx" data-component-line="194" data-component-end-line="197" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-gradient-to-r%20from-gray-800%20via-gray-700%20to-gray-800%20animate-pulse%20rounded%22%7D" 
                    className="absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-pulse rounded"
                    style={{ width: width || 400, height: height || 300 }}
                />
            )}
        </div>
    );
}

export default AppImage;